import React from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { FiMail, FiCalendar } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { RoleTag } from '@/views/EmployeeList/components/RoleTag.component';
import { StatCard, statCardVariants } from '@/components/StatCard';
import { fetchEmployeeExtendedDetails } from '@/services/employeeMemberEnrichment';
import { queryKeys } from '@/config/queryKeys';
import { db } from '@/config/firebase.config';
import DefaultAvatar from '@assets/img/svg/avatar.svg';
import secureProfileIllustration from '@assets/img/illustrations/secure-profile.png';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import { DEMO_MY_EXTENDED_DETAILS } from '@/views/EmployeeList/EmployeeList.demoData';

import styles from './ProfileHeaderCard.module.scss';

export const ProfileHeaderCard = () => {
  const user = useSelector((state) => ({
    profile: state.auth?.profile,
    role: state.auth?.profile?.activeCompany?.role,
  }));
  const userId = user.profile?.id;

  const { data: realExtendedDetails } = useQuery(
    queryKeys.employeeList.extendedDetails(userId),
    () => fetchEmployeeExtendedDetails(db, userId),
    { enabled: !!userId && !IS_DEMO_MODE, staleTime: 5 * 60 * 1000 }
  );
  const extendedDetails = IS_DEMO_MODE
    ? DEMO_MY_EXTENDED_DETAILS
    : realExtendedDetails;

  const memberSince = extendedDetails?.createdAt?.toDate
    ? moment(extendedDetails.createdAt.toDate()).format('DD MMM YYYY')
    : '—';

  return (
    <div className={styles.headerCard}>
      <img
        className={styles.avatar}
        alt="Profile avatar"
        src={
          !isEmpty(user.profile?.avatar) ? user.profile.avatar : DefaultAvatar
        }
      />

      <div className={styles.identity}>
        <div className={styles.nameRow}>
          <Text
            variant={textVariantsEnum.Text_xl}
            fontWeight={fontWeightEnum.Bold}
            color="gray-900"
          >
            {user.profile?.name}
          </Text>
          {user.role && <RoleTag role={user.role} />}
        </div>
        <div className={styles.emailRow}>
          <FiMail size={14} color="var(--gray-500)" />
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-600"
          >
            {user.profile?.email}
          </Text>
        </div>
      </div>

      <div className={styles.statSlot}>
        <StatCard
          variant={statCardVariants.inline}
          icon={<FiCalendar size={16} />}
          label="Member Since"
          value={memberSince}
        />
      </div>

      <img
        className={styles.illustration}
        src={secureProfileIllustration}
        alt=""
        aria-hidden="true"
      />
    </div>
  );
};

ProfileHeaderCard.displayName = 'ProfileHeaderCard';
